import {
    Button,
    FormHelperText,
    Grid,
    MenuItem,
    Select,
    styled,
} from '@mui/material';
import AppModal from 'components/AppModal';
import FlexBox from 'components/flexbox/FlexBox';
import AppTextField from 'components/input-fields/AppTextField';
// import ImageUploadInput from "components/input-fields/ImageUploadInput";
import Scrollbar from 'components/ScrollBar';
import { H2, H6 } from 'components/Typography';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import axiosInstance from 'utils/axios';
import * as Yup from 'yup'; // component props interface

// styled components
const StyledAppModal = styled(AppModal)(({ theme }) => ({
    maxWidth: 600,
    minWidth: 450,
    [theme.breakpoints.down(325)]: {
        maxWidth: '100%',
    },
}));

const AddEmployeeModal = ({
    open,
    onClose,
    edit,
    data,
    onSubmissionSuccess,
}) => {
    const [professionList, setProfessionList] = useState([]);
    const [sponsorList, setSponsorList] = useState([]);
    const initialValues = useMemo(() => {
        return {
            full_name: data?.full_name || '',
            email: data?.email || '',
            phone_number: data?.phone_number || '',
            profession: data?.profession?.id || '',
            role: data?.role || '',
            sponsor: data?.sponsor?.id || '',
        };
    }, [data]);
    const fieldValidationSchema = Yup.object().shape({
        full_name: Yup.string()
            .min(3, 'Too Short')
            .required('Name is Required!'),
        email: Yup.string().required('Email is Required!'),
        // avatar: Yup.string().required("Avatar is Required!"),
        phone_number: Yup.string().required('Phone number is Required!'),
        profession: Yup.string().required('Profession is Required!'),
        role: Yup.string().required('Role is Required!'),
        sponsor: Yup.string(),
    });
    const {
        values,
        errors,
        handleChange,
        handleSubmit,
        touched,
        setValues,
        isSubmitting,
    } = useFormik({
        initialValues,
        validationSchema: fieldValidationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                await axiosInstance.patch(`/api/users/${data.id}`, values);
                onSubmissionSuccess(); // Call the callback function
                onClose();
                toast.success('New Data Added Successfully');
            } catch (error) {
                console.error(error);
            } finally {
                setSubmitting(false);
            }
        },
    });
    const getData = async () => {
        await axiosInstance
            .get('api/profession')
            .then(({ data }) => setProfessionList(data))
            .catch((err) => console.log(err));
        await axiosInstance
            .get('/api/sponsor')
            .then((res) => setSponsorList(res.data))
            .catch((err) => console.log(err));
    };
    useEffect(() => {
        setValues(initialValues);
        getData();
        // eslint-disable-next-line
    }, [setValues, initialValues]);
    return (
        <StyledAppModal open={open} handleClose={onClose}>
            <H2 mb={2}>{edit ? 'Edit User' : 'Add new Employee'}</H2>

            <form onSubmit={handleSubmit}>
                <Scrollbar
                    style={{
                        maxHeight: 400,
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <H6 mb={1}>Full Name</H6>
                            <AppTextField
                                fullWidth
                                size="small"
                                name="full_name"
                                placeholder="Name"
                                value={values.full_name}
                                onChange={handleChange}
                                error={Boolean(
                                    errors.full_name && touched.full_name,
                                )}
                                helperText={
                                    touched.full_name && errors.full_name
                                }
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <H6 mb={1}>Email</H6>
                            <AppTextField
                                fullWidth
                                size="small"
                                name="email"
                                placeholder="uilib@gmail.com"
                                onChange={handleChange}
                                value={values.email}
                                error={Boolean(errors.email && touched.email)}
                                helperText={touched.email && errors.email}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <H6 mb={1}>Phone Number</H6>
                            <AppTextField
                                fullWidth
                                size="small"
                                name="phone_number"
                                placeholder="phone_number"
                                onChange={handleChange}
                                value={values.phone_number}
                                error={Boolean(
                                    errors.phone_number && touched.phone_number,
                                )}
                                helperText={
                                    touched.phone_number && errors.phone_number
                                }
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <H6 mb={1}>Profession</H6>
                            <Select
                                size="small"
                                variant="outlined"
                                name={'profession'}
                                fullWidth
                                value={values.profession}
                                onChange={handleChange}
                            >
                                {professionList &&
                                    professionList.map((item, key) => (
                                        <MenuItem key={key} value={item.id}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                            {touched.profession && (
                                <FormHelperText
                                    error
                                    sx={{
                                        ml: '14px',
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                    }}
                                >
                                    {errors.profession}
                                </FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={6}>
                            <H6 mb={1}>Role</H6>
                            <Select
                                size="small"
                                variant="outlined"
                                name={'role'}
                                fullWidth
                                value={values.role}
                                onChange={handleChange}
                            >
                                <MenuItem key={'1'} value="member">
                                    Member
                                </MenuItem>
                                <MenuItem key={'2'} value="group">
                                    Group
                                </MenuItem>
                                <MenuItem key={'3'} value="operator">
                                    Operator
                                </MenuItem>
                                <MenuItem key={'3'} value="judge">
                                    Judge
                                </MenuItem>
                            </Select>
                            {touched.profession && (
                                <FormHelperText
                                    error
                                    sx={{
                                        ml: '14px',
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                    }}
                                >
                                    {errors.profession}
                                </FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={6}>
                            <H6 mb={1}>Sponsor</H6>
                            <Select
                                size="small"
                                variant="outlined"
                                name={'sponsor'}
                                fullWidth
                                value={values.sponsor}
                                onChange={handleChange}
                            >
                                {sponsorList &&
                                    sponsorList.map((item, key) => (
                                        <MenuItem key={key} value={item.id}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                            {touched.sponsor && (
                                <FormHelperText
                                    error
                                    sx={{
                                        ml: '14px',
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                    }}
                                >
                                    {errors.sponsor}
                                </FormHelperText>
                            )}
                        </Grid>
                    </Grid>
                </Scrollbar>

                <FlexBox justifyContent="flex-end" gap={2} marginTop={4}>
                    <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        fullWidth
                        size="small"
                        type="submit"
                        variant="contained"
                    >
                        Save
                    </Button>
                </FlexBox>
            </form>
        </StyledAppModal>
    );
};

export default AddEmployeeModal;
